<template>
<LayoutSearchFieldVertical
  display-variant="list"
  :body-has-bg-color="true"
>
  <template #header>
    <div class="search-field-time-range">
      <form class="search-keyword-field" @submit.prevent="addWords">
        <input
          class="jps-input jps-keyword-input"
          type="text"
          :placeholder="placeholder"
          v-model="value"
          @keyup.prevent.esc="value = ''"
        />
        <UiButtonIcon
          icon-size="size-5"
          styled="secondary"
          tag="button"
          type="submit"
          icon="mdi/plus-circle"
          :disabled="!value"
        />
      </form>

      <div class="search-field-time-range">
        <span class="time-range-heading">{{
          lss('または西暦で指定', 'Year Range')
        }}</span>
        <form class="search-field-time-range-row" @submit.prevent="addRange">
          <SearchFieldInputWithLabel
            :label="lss('開始年', 'Start year')"
            :id="`temporal-start-year-${field.key}`"
          >
            <input
              class="jps-input jps-keyword-input time-range-input-year"
              type="number"
              min="0"
              v-model="yearFrom"
              @keyup.prevent.esc="yearFrom = null"
            />
          </SearchFieldInputWithLabel>

          <SearchFieldInputWithLabel
            :label="lss('終了年', 'End year')"
            :id="`temporal-end-year-${field.key}`"
          >
            <input
              class="jps-input jps-keyword-input time-range-input-year"
              type="number"
              min="0"
              v-model="yearTo"
              @keyup.prevent.esc="yearTo = null"
            />
          </SearchFieldInputWithLabel>
          <div
            class="time-range-nav"
          >
            <UiButtonIcon
              icon-size="size-5"
              styled="secondary"
              tag="button"
              type="submit"
              icon="mdi/plus-circle"
              :disabled="!yearFrom && !yearTo"
            />
          </div>
        </form>
      </div>
    </div>
    <div v-if="field.samples" class="sample-query">
      <dl>
        <dt class="jps-flex-center">
          {{ ls({ ja: '例', en: 'Example ' }) }}
        </dt>
        <dd v-for="keyword in field.samples" :key="keyword.ja">
          <a class="jps-text-button" @click="addKeyword(keyword.ja)">
            {{ ls(keyword) }}
          </a>
        </dd>
      </dl>
    </div>
  </template>
  <template #default v-if="!isEmpty(facet)">
    <div class="facet-buttons">
      <button
        class="fb-button"
        v-for="era in ERAS"
        :class="{
          'is-0': count(era) === 0,
          'is-exclude': exclude,
          'is-selected': isSelected(era),
        }"
        @click="select(era)"
        :key="era.key"
      >
        <span class="fb-label">{{ display(era) }} </span>
        <span class="fb-count" :title="num(count(era))">{{
          facetNum(count(era))
        }}</span>
      </button>
    </div>
  </template>
  <template #options>
    <BaseToggleSwitch class="is-red" v-model="exclude">{{
      lss('除外条件を指定', 'Exclude')
    }}</BaseToggleSwitch>
  </template>
</LayoutSearchFieldVertical>
</template>

<script lang="ts" setup>
import type { Era } from '~/components/search/useSearchField'
import { ERAS } from '~/components/search/useSearchField'
import { useSearchFieldBase } from '~/components/search/search-field/useBaseSearchField'
import type { SearchFieldBaseProps } from '~/components/search/search-field/useBaseSearchField'

const { ls, lss, num } = useI18n()

interface Props extends SearchFieldBaseProps {
}
const props = withDefaults(defineProps<Props>(), {
})

const { facet, excludeDisplay, facetNum, placeholder } = useSearchFieldBase(props)

const value: Ref<string> = ref('')

const addWords = () => {
  if (value.value) props.ss.setQuery(props.field.key, split(value.value))
  else props.ss.setQuery(props.field.key, [])
}

const addKeyword = (word: string) => {
  props.ss.addQuery(props.field.key, [word])
  value.value = null
}

const yearFrom = ref(null)
const yearTo = ref(null)

const addRange = () => {
  if (yearFrom.value == null && yearTo.value == null) return
  let year = {}
  if (yearFrom.value != null) year['from'] = '' + yearFrom.value
  if (yearTo.value != null) year['to'] = '' + yearTo.value
  props.ss.state.range[props.field.key] = [year]
  props.ss.execute()
}

// ファセット
const exclude: Ref<boolean> = ref(false)

const count = (era: Era) => {
  if (!facet.value) return undefined
  return facet.value.counts[era.key] || 0
}

const display = (era: Era) => {
  return excludeDisplay(ls(era.name), exclude.value, count(era))
}

const select = (era: Era) => {
  let word = era.key
  if (exclude.value) word = '-' + word
  else word = '+' + word
  if (isSelected(era)) {
    props.ss.removeFilter(props.field.key, [word])
  } else {
    props.ss.addFilter(props.field.key, [word])
  }
}

const isSelected = (era: Era) => {
  let word = era.key
  return props.ss.state.filter[props.field.key]?.includes('+' + word)
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.search-field-time-range {
  display: flex;
  gap: 0.25rem 0.75rem;
  flex-wrap: wrap;
  align-items: end;
}

.search-field-time-range-row {
  display: flex;
  gap: 0.25rem 0.5rem;
  flex-wrap: wrap;
  align-items: end;

  .time-range-input-year {
    max-width: 5rem;
    text-align: right;
  }
}

.time-range-select {
  line-height: 1.8;
  margin: 0;
}

.time-range-heading,
.time-range-nav {
  min-height: 2.125rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.sample-query {
  margin-top: 0.25rem;
}
</style>
